import type { ExecutionTradeOverlay } from "../../../../../types/trade";

/** Cumulative PnL band: reflect regime veto (drop removed) or TM sim outcomes when overlays are on. */
export function resolveOverlayAdjustedTradePnl(
  trade: ExecutionTradeOverlay,
  options: { policyOverlayActive: boolean; simulationLineOverlay: boolean }
): number | null {
  if (options.policyOverlayActive && trade.policy_overlay === "removed") {
    return null;
  }

  if (options.simulationLineOverlay) {
    if (trade.simulation_overlay === "changed" && trade.sim_net_pnl != null) {
      return Number(trade.sim_net_pnl);
    }
  }

  if (trade.net_pnl != null) return Number(trade.net_pnl);
  if (trade.entry_price != null && trade.exit_price != null) {
    return trade.direction === "short"
      ? Number(trade.entry_price) - Number(trade.exit_price)
      : Number(trade.exit_price) - Number(trade.entry_price);
  }
  return null;
}

/** Book realized PnL when the trade closes (not when it opens). */
export function resolveTradePnlRealizationTime(trade: ExecutionTradeOverlay): string | null {
  if (trade.simulation_overlay === "changed" && trade.sim_exit_time) {
    return trade.sim_exit_time;
  }
  return trade.exit_time ?? trade.entry_time ?? null;
}

/**
 * Map cumulative dollar PnL to chart prices with $0 on the baseline (neutral).
 *
 * Uses a robust “typical” magnitude (median |PnL|) plus soft tanh compression so
 * small swings stay visible instead of being flattened by a large peak-to-peak range.
 */
export function mapCumulativePnlToBaselineBand(
  cumulative: Array<{ time: number; signal: number }>,
  midPrice: number,
  halfBand: number
): Array<{ time: number; value: number }> {
  if (cumulative.length === 0) return [];

  const absSignals: number[] = [];
  let maxAbs = 0;
  for (const p of cumulative) {
    const abs = Math.abs(p.signal);
    if (abs > 0) absSignals.push(abs);
    if (abs > maxAbs) maxAbs = abs;
  }

  absSignals.sort((a, b) => a - b);
  const medianAbs =
    absSignals.length > 0 ? absSignals[Math.floor(absSignals.length * 0.5)]! : maxAbs;

  // Prefer typical equity level over the peak so micro moves keep slope; keep a
  // floor against maxAbs so a lone spike cannot force an absurd magnification.
  const scaleRef = Math.max(medianAbs * 1.25, maxAbs * 0.18, 1e-9);

  return cumulative.map((p) => ({
    time: p.time,
    value: midPrice + halfBand * Math.tanh(p.signal / scaleRef),
  }));
}
